// ABOUTME: My job's current state: company, place, start and end, weekly hours. As "Aktueller Status" on the my-job page,
// ABOUTME: or, with PJL, as "Dein Job" right under the job's header on the page of the job (`asJobSection`), without the
// ABOUTME: rows the job's header and sections already show the same; and the header's badge saying since when it runs.
import { BoldChip } from 'components/BoldChip';
import { ContentCard } from 'components/Card/ContentCard';
import { ErrorFallbackScreen } from 'components/screens';
import { getTrans, getTransWithParams } from 'modules/i18n';
import {
  JobPageRow,
  JobPageSection
} from 'modules/jobListings/components/JobPageParts';
import type { JobDetails } from 'modules/jobListings/types';
import { AssignmentStatus } from 'modules/status-badge';
import {
  DEFAULT_TIMEZONE,
  getHoursAndMinutesLabel,
  getShortDateLabelWithTimezone
} from 'utils/date';
import { useAssignmentDetails } from '../hooks';
import { berlinDay, jobPageDay, jobPageWeeklyHours } from '../jobPageFormat';
import { AssignmentDetailsData } from '../types';
import { AssignmentDetailsSkeleton } from './AssignmentDetailsSkeleton';
import { DetailsDataItem } from './DetailsDataItem';

const JOB_SECTION_HEADING_ID = 'assignment-job-state-title';

const sameDay = (a?: string, b?: string): boolean =>
  !!a && !!b && berlinDay(a) === berlinDay(b);

/** Whether the job's places name the assignment's place the way the "Wo" section shows it. */
const placeShown = (location: string, job: JobDetails): boolean => {
  const names = (job.workLocations ?? []).flatMap(place => [
    place.name,
    place.city ? `${place.name}, ${place.city}` : undefined
  ]);
  if (job.locationName) {
    names.push(
      job.locationName,
      job.locationCity ? `${job.locationName}, ${job.locationCity}` : undefined
    );
  } else if (!job.workLocations?.length) {
    // Without places and a name, the section shows the city alone.
    names.push(job.locationCity);
  }
  return names.some(name => !!name && name === location);
};

/** Whether the job's contract section shows this end: it shows none for an open-ended contract. */
const endShown = (endDate: string, job?: JobDetails): boolean =>
  !!job && job.unlimitedContract !== true && sameDay(endDate, job.endDate);

/** The job's weekly hours when they are one figure, as its hours section shows them. */
const weeklyMinutesShown = (job: JobDetails): number | undefined => {
  const min = job.minWeeklyHourMinutes ?? job.maxWeeklyHourMinutes;
  const max = job.maxWeeklyHourMinutes ?? job.minWeeklyHourMinutes;
  return min !== undefined && min === max ? min : undefined;
};

/**
 * The rows of "Dein Job": every value the assignment carries, less what the job's header and sections show the same
 * (the company in the header, the place among the places, start, end and weekly hours in the contract and the hours).
 */
const jobStateRows = (
  assignment: AssignmentDetailsData,
  job?: JobDetails
): { label: string; value: string }[] => {
  const shown = {
    company: !!job && job.companyName === assignment.company,
    location: !!job && placeShown(assignment.location, job),
    startDate: sameDay(assignment.startDate, job?.startDate),
    endDate: endShown(assignment.endDate, job),
    weeklyHours:
      !!job && weeklyMinutesShown(job) === assignment.expectedPeriodMinutes
  };
  const candidates: { label: string; value: string | undefined }[] = [
    {
      label: getTrans('assignmentDetailsPage.company'),
      value: shown.company ? undefined : assignment.company
    },
    {
      label: getTrans('assignmentDetailsPage.location'),
      value: shown.location ? undefined : assignment.location
    },
    {
      label: getTrans('assignmentDetailsPage.startDate'),
      value:
        assignment.startDate && !shown.startDate
          ? jobPageDay(assignment.startDate)
          : undefined
    },
    {
      label: getTrans('assignmentDetailsPage.endDate'),
      value:
        assignment.endDate && !shown.endDate
          ? jobPageDay(assignment.endDate)
          : undefined
    },
    {
      label: getTrans('assignmentDetailsPage.weeklyHours'),
      value:
        assignment.expectedPeriodMinutes && !shown.weeklyHours
          ? jobPageWeeklyHours(assignment.expectedPeriodMinutes)
          : undefined
    }
  ];
  return candidates.filter(
    (row): row is { label: string; value: string } => !!row.value
  );
};

/**
 * PJL: the first badge in the job page's header on my job: "Läuft seit 03. Nov. 2026" while it runs, "Dein Job" before
 * it starts or after it ended; nothing until the assignment is read.
 */
export const AssignmentJobBadge = () => {
  const { assignmentDetails } = useAssignmentDetails();
  if (!assignmentDetails) return null;
  const running =
    assignmentDetails.status === AssignmentStatus.ACTIVE &&
    !!assignmentDetails.startDate;
  return (
    <BoldChip
      variant="soft"
      size="small"
      color={running ? 'success' : 'primary'}
      data-testid="assignment-job-badge"
      label={
        running
          ? getTransWithParams('assignmentDetailsPage.jobPage.runningSince', {
              date: jobPageDay(assignmentDetails.startDate)
            })
          : getTrans('assignmentDetailsPage.jobPage.title')
      }
    />
  );
};

/**
 * @param asJobSection PJL: render the state as the "Dein Job" section of the job page, in the sections' style, instead
 * of the "Aktueller Status" card; a value the assignment does not carry is left out rather than shown empty.
 * @param job PJL: the job the page shows under the section; a value it shows the same is not repeated here.
 */
export const AssignmentDetails = ({
  asJobSection = false,
  job
}: {
  asJobSection?: boolean;
  job?: JobDetails;
}) => {
  const { isLoading, assignmentDetails, isError } = useAssignmentDetails();

  if (isLoading) return <AssignmentDetailsSkeleton />;

  if (isError) return <ErrorFallbackScreen />;

  if (!assignmentDetails) return null;

  if (asJobSection) {
    const rows = jobStateRows(assignmentDetails, job);
    // Nothing to add to what the job page shows: no "Dein Job" heading over nothing.
    if (!rows.length) return null;
    return (
      <JobPageSection
        title={getTrans('assignmentDetailsPage.jobPage.title')}
        testId="assignment-job-state"
        headingId={JOB_SECTION_HEADING_ID}
      >
        {rows.map(row => (
          <JobPageRow key={row.label} label={row.label} value={row.value} />
        ))}
      </JobPageSection>
    );
  }

  return (
    <ContentCard title={getTrans('assignmentDetailsPage.currAssignment.title')}>
      <DetailsDataItem
        label={getTrans('assignmentDetailsPage.company')}
        value={assignmentDetails.company}
        sx={{ pt: 1 }}
      />
      <DetailsDataItem
        label={getTrans('assignmentDetailsPage.location')}
        value={assignmentDetails.location}
      />

      <DetailsDataItem
        label={getTrans('assignmentDetailsPage.startDate')}
        value={getShortDateLabelWithTimezone(
          assignmentDetails.startDate,
          DEFAULT_TIMEZONE
        )}
      />
      <DetailsDataItem
        label={getTrans('assignmentDetailsPage.endDate')}
        value={getShortDateLabelWithTimezone(
          assignmentDetails.endDate,
          DEFAULT_TIMEZONE
        )}
      />
      <DetailsDataItem
        label={getTrans('assignmentDetailsPage.weeklyHours')}
        value={getHoursAndMinutesLabel(assignmentDetails.expectedPeriodMinutes)}
        sx={{ p: 0 }}
      />
    </ContentCard>
  );
};
